import React, { useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Flex, Box, Heading, Text, Button, Center, VStack, Icon, IconButton,
  HStack, Badge, Grid, SimpleGrid,
} from "@chakra-ui/react";
import { MdStorefront, MdStore } from "react-icons/md";
import {
  FiHash, FiTag, FiPackage, FiMapPin, FiGrid, FiX,
  FiCheckCircle, FiXCircle, FiUser, FiCalendar, FiClock,
} from "react-icons/fi";

const formatDate = (ts) => {
  if (!ts) return "-";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const d = new Date(ts);
  if (isNaN(d)) return "-";
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${d.getFullYear()}`;
};
const formatTime = (ts) => {
  if (!ts) return "-";
  const part = ts.split("T")[1];
  return part ? part.split(".")[0] : "-";
};
import { InfoCard, ProductTable } from "../cards";
import { VisualEvidence } from "../components/VisualEvidence";
import { CoolerBreakdown } from "../components/CoolerBreakdown";
import ActivationDetails  from "../components/ActivationDetails";

export function DetailModal({
  isOpen,
  onClose,
  isOrgUser,
  storeData = {},
  images = {},
  actions = {},
  editModal = {},
}) {
  const { scoreBreakdown, productSummary, selectedStoreDetails, shelfInfo } = storeData;
  const {
    actualImageUrl, annotatedImageUrl, modalActualImages, modalAnnotatedImages,
    modalImageIndex, onPrevImage, onNextImage, loadingCoolerImage,
    generatePresignedUrls, onViewActivation, onViewCoolerImage,
  } = images;

  
  const {
    onRecalculate, isRecalculating, onValidateAvl, isValidatingAvl,
    onValidateAct, isValidatingAct, onAdjustedQty, onRefresh,
  } = actions;
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose, onSave: onEditSave } = editModal;

  const [showValidationCard, setShowValidationCard] = useState(false);
  const validationInfo = {
    status:         scoreBreakdown?.status,
    userName:       scoreBreakdown?.userName,
    allocationTime: scoreBreakdown?.allocationTime,
    validationTime: scoreBreakdown?.validationTime,
  };
  const isValidated = validationInfo.status === "VALIDATED";

  const store = scoreBreakdown?.store || {};
  const runId = scoreBreakdown?.availability?.products?.[0]?.runId;
  const storeId = scoreBreakdown?.availability?.products?.[0]?.storeId;
  const fileName = scoreBreakdown?.availability?.imagePathResponse
    ?.s3pathActualFile?.[modalImageIndex]?.split("/").pop();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      isCentered
      motionPreset="slideInBottom"
      scrollBehavior="inside"
      blockScrollOnMount={false}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxH="90vh" bg="white" borderRadius="2xl" boxShadow="2xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(to-r, red.600, red.500)"
          color="white" py={6} px={8}
          borderBottom="1px solid" borderColor="red.700"
        >
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={3}>
              <Icon as={MdStorefront} boxSize={7} />
              <Box>
                <Heading size="md" fontWeight="bold">Store Detail View</Heading>
                <Text fontSize="sm" color="red.100" mt={1}>
                  Comprehensive store analytics and visual insights
                </Text>
              </Box>
            </Flex>
            <IconButton
              aria-label="Close modal"
              icon={<FiX />}
              variant="ghost"
              color="white"
              _hover={{ bg: "red.700" }}
              onClick={onClose}
              size="lg"
            />
          </Flex>
        </ModalHeader>

        <ModalBody px={8} py={6} overflowY="scroll">
          {scoreBreakdown ? (
            <VStack spacing={6} align="stretch">
              {/* Store Overview */}
              <Box
                bg="white" borderRadius="xl" border="1px solid"
                borderColor="gray.200" boxShadow="lg" p={6}
                position="relative" overflow="hidden"
                _before={{
                  content: '""', position: "absolute",
                  top: 0, left: 0, width: "6px", height: "100%", bg: "red.600",
                }}
              >
                <Flex align="center" justify="space-between" mb={4}>
                  <Flex align="center">
                    <Icon as={MdStore} color="red.600" boxSize={6} mr={3} />
                    <Heading size="md" color="gray.800" fontWeight="semibold">
                      Store Overview
                    </Heading>
                  </Flex>
                  {validationInfo.status && (
                    isValidated ? (
                      <Button
                        leftIcon={<FiCheckCircle />}
                        colorScheme="green"
                        variant="solid"
                        size="sm"
                        onClick={() => setShowValidationCard((v) => !v)}
                        _hover={{ bg: "green.600", transform: "translateY(-1px)" }}
                        transition="all 0.2s"
                      >
                        Verified
                      </Button>
                    ) : (
                      <Button
                        leftIcon={<FiXCircle />}
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowValidationCard((v) => !v)}
                        _hover={{ bg: "red.50", transform: "translateY(-1px)" }}
                        transition="all 0.2s"
                      >
                        Not Verified
                      </Button>
                    )
                  )}
                </Flex>

                {showValidationCard && (
                  <Box
                    mb={4} p={4} borderRadius="lg" border="1px solid"
                    bg={isValidated ? "green.50" : "red.50"}
                    borderColor={isValidated ? "green.200" : "red.200"}
                  >
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                      <Flex align="center" gap={2}>
                        <Icon as={FiUser} color={isValidated ? "green.600" : "red.500"} boxSize={4} />
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="medium">Username</Text>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {validationInfo.userName || "-"}
                          </Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={2}>
                        <Icon as={FiCalendar} color={isValidated ? "green.600" : "red.500"} boxSize={4} />
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="medium">Allocation Date</Text>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {formatDate(validationInfo.allocationTime)}
                          </Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={2}>
                        <Icon as={FiClock} color={isValidated ? "green.600" : "red.500"} boxSize={4} />
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="medium">Allocation Time</Text>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {formatTime(validationInfo.allocationTime)}
                          </Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={2}>
                        <Icon as={FiCalendar} color={isValidated ? "green.600" : "red.500"} boxSize={4} />
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="medium">Validation Date</Text>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {formatDate(validationInfo.validationTime)}
                          </Text>
                        </Box>
                      </Flex>
                    </SimpleGrid>
                  </Box>
                )}

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  <InfoCard icon={FiHash}    label="Store ID"             value={store.storeId || "-"}          color="red" />
                  <InfoCard icon={FiTag}     label="Store Code"           value={store.storeId || "-"}          color="green" />
                  <InfoCard icon={MdStore}   label="Store Name"           value={store.storeName || "-"}        color="purple" />
                  <InfoCard icon={FiPackage} label="Store Type - Class"   value={`${store.storeType || "-"} - ${store.storeClass || "-"}`} color="orange" />
                  <InfoCard icon={FiPackage} label="Cooler Size"          value={store.coolerSize || "-"}       color="blue" />
                  <InfoCard icon={FiMapPin}  label="Address"              value={store.storeAddress || "-"}     color="red" colSpan={{ base: 1, md: 2, lg: 3 }} />
                </SimpleGrid>
              </Box>

              <Grid templateColumns={{ base: "1fr", lg: "1fr" }} gap={6} minH="500px">
                {/* Product Summary */}
                <Box
                  bg="white" borderRadius="xl" border="1px solid"
                  borderColor="gray.200" boxShadow="lg" p={6}
                  display="flex" flexDirection="column" height="100%"
                >
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center">
                      <Icon as={FiGrid} color="red.600" boxSize={6} mr={3} />
                      <Heading size="md" color="gray.800" fontWeight="semibold">
                        Product Summary
                      </Heading>
                    </Flex>
                    <HStack spacing={3} align="center">
                      <Badge colorScheme="green" variant="subtle" px={3} py={1} fontSize="md">
                        availabilityScore:{" "}
                        {scoreBreakdown?.availability?.availabilityScore
                          ? `${scoreBreakdown.availability.availabilityScore} out of 70`
                          : "0 out of 70"}
                      </Badge>
                      <Badge colorScheme="red" variant="subtle" px={3} py={1} borderRadius="full">
                        {productSummary.length} items
                      </Badge>
                    </HStack>
                  </Flex>

                  <Box>
                    {productSummary.length > 0 ? (
                      <Box
                        key={selectedStoreDetails?.storeId}
                        borderRadius="lg" overflow="hidden"
                        border="1px solid" borderColor="gray.200"
                      >
                        <ProductTable productSummary={productSummary} />
                      </Box>
                    ) : (
                      <Center h="400px">
                        <VStack spacing={3}>
                          <Icon as={FiPackage} boxSize={12} color="gray.400" />
                          <Text color="gray.600" fontWeight="medium">No products found</Text>
                          <Text fontSize="sm" color="gray.500">
                            No product data available for this store
                          </Text>
                        </VStack>
                      </Center>
                    )}
                  </Box>

                  <Flex justify="flex-end" mt={4}>
                    <Button
                      colorScheme="red" variant="outline" size="sm"
                      onClick={onAdjustedQty}
                      _hover={{ bg: "red.50", transform: "translateY(-2px)", boxShadow: "md" }}
                      transition="all 0.2s"
                    >
                      Adjusted Qty
                    </Button>
                  </Flex>
                </Box>

                {/* Visual Evidence */}
                <VisualEvidence
                  isOrgUser={isOrgUser}
                  actualImageUrl={actualImageUrl}
                  annotatedImageUrl={annotatedImageUrl}
                  modalActualImages={modalActualImages}
                  modalAnnotatedImages={modalAnnotatedImages}
                  modalImageIndex={modalImageIndex}
                  shelfInfo={shelfInfo}
                  onPrev={onPrevImage}
                  onNext={onNextImage}
                  onRecalculate={onRecalculate}
                  isRecalculating={isRecalculating}
                  onValidateAvl={onValidateAvl}
                  isValidatingAvl={isValidatingAvl}
                  isEditOpen={isEditOpen}
                  onEditOpen={onEditOpen}
                  onEditClose={onEditClose}
                  onEditSave={onEditSave}
                  runId={runId}
                  storeId={storeId}
                  fileName={fileName}
                  auditDate={selectedStoreDetails?.auditedDate}
                  validationInfo={{
                    status:         scoreBreakdown?.status,
                    userName:       scoreBreakdown?.userName,
                    allocationTime: scoreBreakdown?.allocationTime,
                    validationTime: scoreBreakdown?.validationTime,
                  }}
                />
              </Grid>

              <CoolerBreakdown scoreBreakdown={scoreBreakdown}
              onViewCoolerImage={onViewCoolerImage}
              loadingCoolerImage={loadingCoolerImage}
               />
              <ActivationDetails
                scoreBreakdown={scoreBreakdown}
                onViewActivation={onViewActivation}
                onValidateAct={onValidateAct}
                isValidatingAct={isValidatingAct}
                selectedStoreDetails={selectedStoreDetails} 
                generatePresignedUrls={generatePresignedUrls}
                onRefresh={onRefresh}
              />
            </VStack>
          ) : (
            <Center h="400px">
              <VStack spacing={4}>
                <Icon as={MdStore} boxSize={16} color="gray.400" />
                <Text fontSize="lg" color="gray.600" fontWeight="semibold">
                  No Store Data Available
                </Text>
                <Text color="gray.500" textAlign="center" maxW="md">
                  Select a store and open detail view to see comprehensive analytics
                </Text>
              </VStack>
            </Center>
          )}
        </ModalBody>

        <ModalFooter px={8} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
          <Flex w="100%" justify="space-between" align="center">
            <Text fontSize="sm" color="gray.600">
              &copy; 2025 Image Insight. All rights reserved.
            </Text>
            <Button
              variant="outline" colorScheme="gray" leftIcon={<FiX />}
              onClick={onClose} size="lg" px={8} borderRadius="lg"
            >
              Close
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
